import type {
  KeyboardEvent as ReactKeyboardEvent,
  MouseEvent as ReactMouseEvent,
} from "react";
import {
  Box,
  Checkbox,
  Table,
  TableBody,
  TableContainer,
  TableHead,
  TableRow,
  Tooltip,
} from "@mui/material";
import { styled } from "@mui/material/styles";
import { useLocation, useSearchParams } from "react-router-dom";
import Warning from "../../../icons/Warning";
import { createConditionLink } from "../../utils/conditions";
import { NoDataText } from "../../components/SectionComponents";
import { TableCell, TableHeadCell } from "../../components/TableCell";
import ConditionNameLink from "./ConditionNameLink";
import { ConditionStatusEntry } from "./statusState";

const ConditionsTableContainer = styled(TableContainer)(() => ({
  border: "1px solid #e0e0e0",
  borderRadius: "4px",
  overflow: "hidden",
  boxSizing: "border-box",
  width: "100%",
  maxWidth: "100%",
}));

type ConditionSelectTableProps = {
  entries: ConditionStatusEntry[];
  selectedIds: number[];
  selectableIds: number[];
  areAllSelected: boolean;
  onToggle: (conditionId: number) => void;
  onToggleAll: () => void;
  isReadOnly?: boolean;
  hasError?: (condition: FormspecCondition) => boolean;
  isLoading?: boolean;
  emptyLabel: string;
  selectAllAriaLabel: string;
  fixedLayout?: boolean;
};

/**
 * Shared checkbox condition table used by both the WIP and Submitted columns.
 * Unifies the two disable models: the WIP column passes `hasError` + `isLoading`
 * to block invalid/loading rows, while the staged sections only pass
 * `isReadOnly`.
 */
const ConditionSelectTable = ({
  entries,
  selectedIds,
  selectableIds,
  areAllSelected,
  onToggle,
  onToggleAll,
  isReadOnly = false,
  hasError,
  isLoading = false,
  emptyLabel,
  selectAllAriaLabel,
  fixedLayout = false,
}: ConditionSelectTableProps) => {
  const location = useLocation();
  const [searchParams] = useSearchParams();

  const stopPropagation = (event: ReactMouseEvent) => event.stopPropagation();

  const handleRowKeyDown = (
    event: ReactKeyboardEvent<HTMLTableRowElement>,
    conditionId: number
  ) => {
    if (event.key === "Enter" || event.key === " ") {
      event.preventDefault();
      onToggle(conditionId);
    }
  };

  return (
    <ConditionsTableContainer>
      <Table
        sx={fixedLayout ? { tableLayout: "fixed", width: "100%" } : undefined}
      >
        <TableHead>
          <TableRow>
            <TableHeadCell sx={{ width: "25px" }}>
              <Checkbox
                color="secondary"
                checked={areAllSelected && selectableIds.length > 0}
                indeterminate={
                  !areAllSelected &&
                  selectableIds.some((id) => selectedIds.includes(id))
                }
                onChange={onToggleAll}
                disabled={selectableIds.length === 0 || isReadOnly}
                inputProps={{ "aria-label": selectAllAriaLabel }}
                sx={{ padding: 0 }}
              />
            </TableHeadCell>
            <TableHeadCell>Condition Name</TableHeadCell>
          </TableRow>
        </TableHead>
        <TableBody>
          {entries.length === 0 ? (
            <TableRow>
              <TableCell
                colSpan={2}
                sx={{ textAlign: "center", padding: "16px" }}
              >
                <NoDataText sx={{ backgroundColor: "transparent", padding: 0 }}>
                  {emptyLabel}
                </NoDataText>
              </TableCell>
            </TableRow>
          ) : (
            entries.map(({ condition }) => {
              const conditionHasError = hasError?.(condition) ?? false;
              const isRowDisabled =
                conditionHasError || isLoading || isReadOnly;
              return (
                <TableRow
                  key={condition.cell_condition_id}
                  onClick={() =>
                    !isRowDisabled && onToggle(condition.cell_condition_id)
                  }
                  onKeyDown={(event) =>
                    !isRowDisabled &&
                    handleRowKeyDown(event, condition.cell_condition_id)
                  }
                  role="button"
                  tabIndex={0}
                  aria-pressed={selectedIds.includes(
                    condition.cell_condition_id
                  )}
                  sx={{
                    opacity: conditionHasError || isLoading ? 0.6 : 1,
                    cursor: isRowDisabled ? "not-allowed" : "pointer",
                    backgroundColor: "#fff",
                    "&:hover": {
                      backgroundColor: isRowDisabled ? "#fff" : "#f5f5f5",
                    },
                  }}
                >
                  <TableCell sx={{ width: "25px" }}>
                    <Checkbox
                      color="secondary"
                      checked={selectedIds.includes(
                        condition.cell_condition_id
                      )}
                      disabled={isRowDisabled}
                      onClick={stopPropagation}
                      onChange={() => onToggle(condition.cell_condition_id)}
                      inputProps={{ "aria-label": `Select ${condition.name}` }}
                      sx={{ padding: 0 }}
                    />
                  </TableCell>
                  <TableCell sx={{ wordBreak: "break-word" }}>
                    <Box
                      sx={{ display: "flex", alignItems: "center", gap: 1.5 }}
                    >
                      {conditionHasError && (
                        <Tooltip
                          title="Resolve all errors before submitting"
                          placement="top"
                          arrow
                        >
                          <span
                            style={{
                              display: "inline-flex",
                              alignItems: "center",
                            }}
                          >
                            <Warning
                              style={{ fontSize: "16px", color: "#ff9800" }}
                            />
                          </span>
                        </Tooltip>
                      )}
                      <ConditionNameLink
                        name={condition.name}
                        to={createConditionLink(
                          location.pathname,
                          searchParams,
                          condition.cell_condition_id
                        )}
                        hasViolations={false}
                        onClick={stopPropagation}
                      />
                    </Box>
                  </TableCell>
                </TableRow>
              );
            })
          )}
        </TableBody>
      </Table>
    </ConditionsTableContainer>
  );
};

export default ConditionSelectTable;
